import type { Project, JournalPost, ContactInfo } from '@/types/content';

const SITE = 'https://brianellisondesign.com';

interface InteriorDesignerSchema {
  '@context': 'https://schema.org';
  '@type': 'InteriorDesigner';
  name: string;
  url: string;
  email?: string;
  telephone?: string;
  address?: {
    '@type': 'PostalAddress';
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    postalCode?: string;
    addressCountry?: string;
  };
  sameAs?: string[];
}

export function buildInteriorDesignerSchema(contact: ContactInfo): InteriorDesignerSchema {
  const sameAs: string[] = [];
  if (contact.social.instagram) sameAs.push(`https://instagram.com/${contact.social.instagram.replace(/^@/, '')}`);
  if (contact.social.houzz) sameAs.push(contact.social.houzz);

  return {
    '@context': 'https://schema.org',
    '@type': 'InteriorDesigner',
    name: contact.company,
    url: SITE,
    email: contact.email,
    telephone: contact.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: contact.address.street,
      addressLocality: contact.address.city,
      addressRegion: contact.address.region,
      postalCode: contact.address.postalCode,
      addressCountry: contact.address.country,
    },
    sameAs: sameAs.length ? sameAs : undefined,
  };
}

interface ProjectSchema {
  '@context': 'https://schema.org';
  '@type': 'CreativeWork';
  name: string;
  description: string;
  dateCreated: string;
  creator: { '@type': 'Person'; name: string };
  locationCreated?: { '@type': 'Place'; name: string };
  image?: string;
  url: string;
}

export function buildProjectSchema(project: Project, contact: ContactInfo): ProjectSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: project.title,
    description: project.summary,
    dateCreated: String(project.year),
    creator: { '@type': 'Person', name: contact.owner },
    locationCreated: project.location ? { '@type': 'Place', name: project.location } : undefined,
    image: project.heroImage ? `${SITE}${project.heroImage}` : undefined,
    url: `${SITE}/projects/${project.slug}`,
  };
}

interface ArticleSchema {
  '@context': 'https://schema.org';
  '@type': 'Article';
  headline: string;
  description: string;
  datePublished: string;
  author: { '@type': 'Person'; name: string };
  url: string;
}

export function buildArticleSchema(post: JournalPost): ArticleSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    datePublished: post.date,
    author: { '@type': 'Person', name: post.author },
    url: `${SITE}/journal/${post.slug}`,
  };
}

interface BreadcrumbSchema {
  '@context': 'https://schema.org';
  '@type': 'BreadcrumbList';
  itemListElement: Array<{
    '@type': 'ListItem';
    position: number;
    name: string;
    item: string;
  }>;
}

export function buildBreadcrumbSchema(crumbs: Array<{ name: string; path: string }>): BreadcrumbSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: crumbs.map((c, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: c.name,
      item: `${SITE}${c.path}`,
    })),
  };
}

interface WebSiteSchema {
  '@context': 'https://schema.org';
  '@type': 'WebSite';
  name: string;
  url: string;
  potentialAction?: object;
}

export function buildWebSiteSchema(): WebSiteSchema {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Brian Ellison Design',
    url: SITE,
  };
}

/** Combine multiple schemas into a single JSON-LD graph. */
export function buildSchemaGraph(...schemas: object[]): { '@context': string; '@graph': object[] } {
  return {
    '@context': 'https://schema.org',
    '@graph': schemas.map((s) => {
      // Strip nested @context since the graph provides it
      const { '@context': _ctx, ...rest } = s as { '@context'?: string };
      return rest;
    }),
  };
}

export function jsonLdScript(schema: object): string {
  // Backslash escape to avoid </script> breakout inside the JSON string
  return JSON.stringify(schema).replace(/</g, '\\u003c');
}
